import { useEffect, useMemo, useState } from "react";
import { format, addDays, differenceInDays, startOfMonth, startOfWeek, isToday } from "date-fns";
import { CaretLeft, CaretRight, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const DAYS = 14;

const PILL_COLORS = {
  confirmed: "bg-status-confirmed/15 border-status-confirmed/50 text-status-confirmed",
  pending: "bg-status-pending/15 border-status-pending/50 text-status-pending",
  checked_in: "bg-status-checked-in/15 border-status-checked-in/50 text-status-checked-in",
  checked_out: "bg-muted border-border text-muted-foreground",
};

function TapeChart({ startDate, setStartDate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/calendar?start=${format(startDate, "yyyy-MM-dd")}&days=${DAYS}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startDate is the only reactive input; DAYS/api/format are stable module-level references
  }, [startDate]);

  const days = useMemo(
    () => Array.from({ length: DAYS }, (_, i) => addDays(startDate, i)),
    [startDate]
  );

  if (!data)
    return <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  return (
    <div data-testid="tape-chart">
      <div className="mb-3 flex items-center gap-2">
        <Button variant="outline" size="sm" data-testid="tape-prev" onClick={() => setStartDate(addDays(startDate, -7))}>
          <CaretLeft size={14} /> Prev 7d
        </Button>
        <Button variant="outline" size="sm" data-testid="tape-today" onClick={() => setStartDate(new Date())}>Today</Button>
        <Button variant="outline" size="sm" data-testid="tape-next" onClick={() => setStartDate(addDays(startDate, 7))}>
          Next 7d <CaretRight size={14} />
        </Button>
        <div className="ml-3 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-status-confirmed" /> Confirmed</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-status-checked-in" /> Checked in</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-status-pending" /> Pending</span>
        </div>
      </div>

      <div className="overflow-x-auto border border-border bg-card">
        <div className="min-w-[1250px]">
          <div className="grid border-b border-border" style={{ gridTemplateColumns: "200px 1fr" }}>
            <div className="sticky left-0 z-10 border-r border-border bg-muted/50 p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Room
            </div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${DAYS}, minmax(0,1fr))` }}>
              {days.map((d) => (
                <div key={d.toISOString()}
                  className={`border-r border-border p-2 text-center text-xs last:border-r-0 ${isToday(d) ? "bg-primary/10 font-bold text-primary" : "text-muted-foreground"}`}>
                  <div className="font-semibold">{format(d, "EEE")}</div>
                  <div>{format(d, "dd MMM")}</div>
                </div>
              ))}
            </div>
          </div>

          {data.rooms.map((room) => {
            const roomBookings = data.bookings.filter((b) => b.room_id === room.id);
            return (
              <div key={room.id} className="grid border-b border-border last:border-b-0" style={{ gridTemplateColumns: "200px 1fr" }} data-testid={`tape-row-${room.id}`}>
                <div className="sticky left-0 z-10 border-r border-border bg-card p-3">
                  <div className="font-heading text-sm font-bold">Room {room.name}</div>
                  <div className="text-xs capitalize text-muted-foreground">{room.type} · {room.status}</div>
                </div>
                <div className="relative h-14">
                  <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${DAYS}, minmax(0,1fr))` }}>
                    {days.map((d) => (
                      <div key={d.toISOString()} data-testid={`tape-cell-${room.id}-${format(d, "yyyy-MM-dd")}`}
                        className={`border-r border-border last:border-r-0 ${isToday(d) ? "bg-primary/5" : ""}`} />
                    ))}
                  </div>
                  {roomBookings.map((b) => {
                    const offset = Math.max(0, differenceInDays(new Date(`${b.check_in}T00:00:00`), startDate));
                    const endOff = Math.min(DAYS, differenceInDays(new Date(`${b.check_out}T00:00:00`), startDate));
                    const span = endOff - offset;
                    if (span <= 0) return null;
                    return (
                      <div key={b.id}
                        data-testid={`tape-booking-${b.id}`}
                        title={`${b.guest_name} · ${b.check_in} → ${b.check_out}`}
                        className={`absolute top-2 flex h-10 items-center gap-1 overflow-hidden rounded-full border px-3 text-xs font-medium ${PILL_COLORS[b.status] || PILL_COLORS.pending}`}
                        style={{ left: `calc(${(offset / DAYS) * 100}% + 3px)`, width: `calc(${(span / DAYS) * 100}% - 6px)` }}>
                        <span className="truncate">{b.guest_name}</span>
                        {b.sync_status === "synced" && <ArrowsClockwise size={12} weight="bold" className="shrink-0" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {data.rooms.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">No rooms yet — add rooms from the Rooms page.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MonthCalendar({ bookings }) {
  const [month, setMonth] = useState(new Date());
  const monthStart = startOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  return (
    <div data-testid="month-calendar">
      <div className="mb-3 flex items-center gap-2">
        <Button variant="outline" size="sm" data-testid="month-prev" onClick={() => setMonth(addDays(startOfMonth(month), -1))}>
          <CaretLeft size={14} /> Prev
        </Button>
        <div className="font-heading text-lg font-bold" data-testid="month-label">{format(month, "MMMM yyyy")}</div>
        <Button variant="outline" size="sm" data-testid="month-next" onClick={() => setMonth(addDays(addDays(startOfMonth(month), 32), 0))}>
          Next <CaretRight size={14} />
        </Button>
      </div>
      <div className="border border-border bg-card">
        <div className="grid grid-cols-7 border-b border-border">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="border-r border-border p-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground last:border-r-0">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d) => {
            const dStr = format(d, "yyyy-MM-dd");
            const inMonth = d.getMonth() === month.getMonth();
            const dayBookings = (bookings || []).filter((b) => b.check_in <= dStr && b.check_out > dStr);
            return (
              <div key={dStr} data-testid={`month-cell-${dStr}`}
                className={`min-h-28 border-b border-r border-border p-1.5 [&:nth-child(7n)]:border-r-0 ${inMonth ? "" : "bg-muted/30 text-muted-foreground"} ${isToday(d) ? "bg-primary/5" : ""}`}>
                <div className={`text-xs font-semibold ${isToday(d) ? "text-primary" : ""}`}>{format(d, "d")}</div>
                <div className="mt-1 space-y-0.5">
                  {dayBookings.slice(0, 3).map((b) => (
                    <div key={b.id} data-testid={`month-booking-${b.id}`}
                      className={`truncate rounded-sm border px-1 py-0.5 text-[10px] font-medium ${PILL_COLORS[b.status] || PILL_COLORS.pending}`}>
                      {b.guest_name} · R{b.room_name}
                    </div>
                  ))}
                  {dayBookings.length > 3 && <div className="text-[10px] text-muted-foreground">+{dayBookings.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [startDate, setStartDate] = useState(new Date());
  const [monthBookings, setMonthBookings] = useState([]);

  useEffect(() => {
    const start = format(addDays(new Date(), -45), "yyyy-MM-dd");
    api.get(`/calendar?start=${start}&days=42`)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot load on mount; api/format/addDays are stable module-level references
  }, []);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Booking Calendar</h1>
        <p className="text-sm text-muted-foreground">Tape chart and month views of every reservation.</p>
      </div>
      <Tabs defaultValue="tape">
        <TabsList data-testid="calendar-view-tabs">
          <TabsTrigger value="tape" data-testid="tab-tape-chart">Tape Chart</TabsTrigger>
          <TabsTrigger value="month" data-testid="tab-month-view">Month View</TabsTrigger>
        </TabsList>
        <TabsContent value="tape" className="mt-4">
          <TapeChart startDate={startDate} setStartDate={setStartDate} />
        </TabsContent>
        <TabsContent value="month" className="mt-4">
          <MonthCalendar bookings={monthBookings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
